import { useEffect, useState } from 'react';
import api from '../lib/api';
import { ROLE_LABELS, ROLE_OPTIONS, fmtDate, fmtDateTime } from '../lib/utils';
import { Plus, MailCheck, Archive, RotateCcw, Trash2, KeyRound, ShieldOff, ShieldCheck, Unlock, Edit, History } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import OrgProfileCard from '../components/OrgProfileCard';
import DataSyncCard from '../components/DataSyncCard';
import CrmCleanupCard from '../components/CrmCleanupCard';

function scorePw(pw) {
  let s = 0;
  if (pw?.length >= 12) s++;
  if (/[A-Z]/.test(pw || '')) s++;
  if (/[a-z]/.test(pw || '')) s++;
  if (/\d/.test(pw || '')) s++;
  if (/[^A-Za-z0-9]/.test(pw || '')) s++;
  return s;
}

export default function SettingsPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [u, setU] = useState({ email: '', password: '', full_name: '', role: 'team_member', title: '' });

  // Dialog states
  const [resetTarget, setResetTarget] = useState(null); // user
  const [resetPw, setResetPw] = useState('');
  const [forceChange, setForceChange] = useState(true);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', title: '', role: '', email: '' });

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => api.get('/users?include_archived=true').then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!u.email || !u.full_name) return toast.error('Email and name required');
    if (u.password && scorePw(u.password) < 5) return toast.error('Password too weak — 12+ chars, upper, lower, number, special');
    try {
      await api.post('/auth/register', { ...u, password: u.password || null });
      toast.success('User created — welcome email with password setup link sent');
      setOpen(false);
      setU({ email: '', password: '', full_name: '', role: 'team_member', title: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const doReset = async () => {
    if (scorePw(resetPw) < 5) return toast.error('Password must meet complexity requirements');
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: resetPw, force_change_on_next_login: forceChange });
      toast.success('Password reset');
      setResetTarget(null); setResetPw(''); setForceChange(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
  };

  const doForceReset = async (user) => {
    try {
      await api.post(`/admin/users/${user.id}/force-password-reset`);
      toast.success(`${user.full_name} will be prompted to change password on next login`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const activate = async (user) => {
    try { await api.post(`/admin/users/${user.id}/activate`); toast.success('Activated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deactivate = async (user) => {
    if (!confirm(`Deactivate ${user.full_name}? They will not be able to log in.`)) return;
    try { await api.post(`/admin/users/${user.id}/deactivate`); toast.success('Deactivated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const unlock = async (user) => {
    try { await api.post(`/admin/users/${user.id}/unlock`); toast.success('Unlocked'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const doEdit = async () => {
    try {
      const payload = Object.fromEntries(Object.entries(editForm).filter(([, v]) => v));
      await api.put(`/admin/users/${editTarget.id}`, payload);
      toast.success('Profile updated');
      setEditTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openHistory = async (user) => {
    setHistoryTarget(user);
    try {
      const r = await api.get(`/admin/users/${user.id}/login-history`);
      setHistory(r.data);
    } catch { setHistory([]); }
  };

  const archiveUser = async (user) => {
    if (!confirm(`Archive ${user.full_name}? They will not appear in default lists.`)) return;
    try { await api.post(`/users/${user.id}/archive`); toast.success('Archived'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const restoreUser = async (user) => {
    try { await api.post(`/users/${user.id}/restore`); toast.success('Restored'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success('Deleted');
      setDeleteTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const canManageLeadership = me?.role === 'leadership';

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-navy">User Management</h2>
          <p className="text-sm text-slate-500">Manage accounts, roles, passwords, and access</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            data-testid="trigger-digests"
            onClick={async () => {
              try {
                const r = await api.post('/digest/send-all-now');
                toast.success(`Digest sent to ${r.data.sent} users`);
              } catch { toast.error('Could not trigger digests'); }
            }}
          >
            <MailCheck className="w-4 h-4 mr-2" /> Send Today&apos;s Digests
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-magenta hover:bg-magenta/90" data-testid="add-user-button"><Plus className="w-4 h-4 mr-2" />Add User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Full Name</Label><Input value={u.full_name} onChange={(e) => setU({ ...u, full_name: e.target.value })} data-testid="user-name" /></div>
                <div><Label className="text-xs">Email</Label><Input type="email" value={u.email} onChange={(e) => setU({ ...u, email: e.target.value })} data-testid="user-email" /></div>
                <div>
                  <Label className="text-xs">Temporary Password (optional — leave blank to email a secure setup link)</Label>
                  <Input type="password" value={u.password} onChange={(e) => setU({ ...u, password: e.target.value })} data-testid="user-password" placeholder="Leave blank (recommended)" />
                  {u.password && (
                    <div className="mt-1 flex gap-1">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className={`h-1 flex-1 rounded ${i < scorePw(u.password) ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 mt-1">A welcome email with login instructions and a password setup link is always sent to the new user.</p>
                </div>
                <div><Label className="text-xs">Title</Label><Input value={u.title} onChange={(e) => setU({ ...u, title: e.target.value })} /></div>
                <div>
                  <Label className="text-xs">Role</Label>
                  <Select value={u.role} onValueChange={(v) => setU({ ...u, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-magenta hover:bg-magenta/90" data-testid="save-user">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-6 py-3">Name</th>
              <th className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-6 py-3">Email</th>
              <th className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-6 py-3">Role</th>
              <th className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-6 py-3">Status</th>
              <th className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-6 py-3">Joined</th>
              <th className="text-right text-xs font-heading uppercase tracking-wider text-slate-500 px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const isLocked = user.locked_until && user.locked_until > new Date().toISOString();
              const isInactive = user.is_active === false;
              return (
                <tr key={user.id} className={`border-t border-slate-100 hover:bg-slate-50 ${user.archived ? 'opacity-60 italic' : ''}`} data-testid={`user-row-${user.id}`}>
                  <td className="px-6 py-3 font-semibold text-sm text-navy">
                    {user.full_name}
                    {user.must_change_password && <span className="ml-2 text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded not-italic">MUST RESET</span>}
                  </td>
                  <td className="px-6 py-3 text-sm">{user.email}</td>
                  <td className="px-6 py-3 text-sm">
                    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-full ${user.role === 'leadership' ? 'bg-magenta/10 text-magenta' : user.role === 'director' ? 'bg-navy/10 text-navy' : 'bg-slate-100 text-slate-700'}`}>
                      {ROLE_LABELS[user.role] || user.role}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-sm">
                    <div data-testid={`user-status-${user.id}`}>
                      {user.account_status === 'archived' ? <span className="text-rose-500 font-bold text-xs">ARCHIVED</span>
                        : user.account_status === 'inactive' ? <span className="text-slate-500 font-bold text-xs">INACTIVE</span>
                        : user.account_status === 'locked' || isLocked ? <span className="text-amber-600 font-bold text-xs">LOCKED</span>
                        : user.account_status === 'pending' ? <span className="text-sky-600 font-bold text-xs">PENDING</span>
                        : <span className="text-emerald-600 font-bold text-xs">ACTIVE</span>}
                      {user.status_changed_at && user.account_status !== 'active' && (
                        <div className="text-[10px] text-slate-400 not-italic mt-0.5">
                          by {user.status_changed_by_name || '—'} · {fmtDate(user.status_changed_at)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-600">{fmtDate(user.created_at)}</td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" data-testid={`user-actions-${user.id}`}>Actions</Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel>Manage {user.full_name}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => { setEditTarget(user); setEditForm({ full_name: user.full_name, title: user.title || '', role: user.role, email: user.email }); }} data-testid={`edit-user-${user.id}`}>
                          <Edit className="w-4 h-4 mr-2" /> Edit profile
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setResetTarget(user); setResetPw(''); setForceChange(true); }} data-testid={`reset-password-${user.id}`}>
                          <KeyRound className="w-4 h-4 mr-2" /> Reset password
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doForceReset(user)} data-testid={`force-reset-${user.id}`}>
                          <KeyRound className="w-4 h-4 mr-2" /> Force password reset
                        </DropdownMenuItem>
                        {isLocked && <DropdownMenuItem onClick={() => unlock(user)} data-testid={`unlock-${user.id}`}><Unlock className="w-4 h-4 mr-2" /> Unlock account</DropdownMenuItem>}
                        {canManageLeadership && (
                          isInactive
                            ? <DropdownMenuItem onClick={() => activate(user)} data-testid={`activate-${user.id}`}><ShieldCheck className="w-4 h-4 mr-2" /> Activate account</DropdownMenuItem>
                            : <DropdownMenuItem onClick={() => deactivate(user)} data-testid={`deactivate-${user.id}`}><ShieldOff className="w-4 h-4 mr-2" /> Deactivate account</DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => openHistory(user)} data-testid={`login-history-${user.id}`}>
                          <History className="w-4 h-4 mr-2" /> Login history
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {user.archived
                          ? <DropdownMenuItem onClick={() => restoreUser(user)} data-testid={`restore-user-${user.id}`}><RotateCcw className="w-4 h-4 mr-2" /> Restore</DropdownMenuItem>
                          : <DropdownMenuItem onClick={() => archiveUser(user)} data-testid={`archive-user-${user.id}`}><Archive className="w-4 h-4 mr-2" /> Archive</DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => setDeleteTarget(user)} className="text-rose-600" data-testid={`delete-user-${user.id}`}>
                          <Trash2 className="w-4 h-4 mr-2" /> Delete permanently
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <OrgProfileCard />

      <CrmCleanupCard />

      {me?.role === 'leadership' && <DataSyncCard />}

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password — {resetTarget?.full_name}</DialogTitle>
            <DialogDescription>Set a new password for this user. They will be notified via email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs">New password (12+ chars, upper, lower, number, special)</Label>
            <Input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} data-testid="admin-reset-password-input" />
            <div className="flex gap-1">
              {[0, 1, 2, 3, 4].map((i) => (<div key={i} className={`h-1 flex-1 rounded ${i < scorePw(resetPw) ? 'bg-emerald-400' : 'bg-slate-200'}`} />))}
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={forceChange} onChange={(e) => setForceChange(e.target.checked)} data-testid="force-change-toggle" />
              Force user to change password on next login
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
            <Button onClick={doReset} className="bg-magenta hover:bg-magenta/90" data-testid="confirm-reset-password">Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit profile dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit profile — {editTarget?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Full name</Label><Input value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} data-testid="edit-user-name" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email" /></div>
            <div><Label className="text-xs">Title</Label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-user-title" /></div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={doEdit} className="bg-magenta hover:bg-magenta/90" data-testid="save-edit-user">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-600">Delete {deleteTarget?.full_name}?</DialogTitle>
            <DialogDescription>
              This will <b>permanently delete</b> this user account. This cannot be undone. Consider archiving instead if you want to keep audit history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-user">Yes, permanently delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Login history */}
      <Dialog open={!!historyTarget} onOpenChange={(v) => !v && setHistoryTarget(null)}>
        <DialogContent className="max-w-2xl" data-testid="login-history-dialog">
          <DialogHeader><DialogTitle>Login history — {historyTarget?.full_name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {history.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No login events recorded yet.</div>}
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b">
                <tr><th className="text-left py-2">When</th><th className="text-left">Result</th><th className="text-left">IP</th><th className="text-left">Browser</th></tr>
              </thead>
              <tbody>
                {history.map((e) => (
                  <tr key={e.id} className="border-b border-slate-50">
                    <td className="py-2 text-slate-600">{fmtDateTime(e.created_at)}</td>
                    <td className={`py-2 font-semibold ${e.success ? 'text-emerald-600' : 'text-rose-600'}`}>{e.success ? 'Success' : e.reason || 'Failed'}</td>
                    <td className="py-2 text-slate-500 text-xs">{e.ip || '—'}</td>
                    <td className="py-2 text-slate-500 text-xs">{e.browser || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
